import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {getApiService} from '@/services/api';

// Define types
interface User {
  id: number;
  username: string;
  email: string;
  roles: string[];
  accessToken: string;
  tokenType: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Define initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Create async thunk for login
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      if (!username || !password) {
        return rejectWithValue('Username and password are required');
      }

      try {
        // Get the API service
        const apiService = getApiService();

        // Make the login request using the API service
        const response = await apiService.client.post<User>(
          '/auth/signin', 
          { username, password }
        );

        // Return the data directly as it now matches our User interface
        return response.data;
      } catch (apiError: any) {
        // Handle API errors
        return rejectWithValue(apiError.message || 'Authentication failed');
      }
    } catch (error) {
      return rejectWithValue('Login failed. Please try again.');
    }
  }
);

// Create the auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
